import {
  EDITOR_EMPTY_FIELD_ATTR,
  EDITOR_FILLER_ATTR,
  EDITOR_RULE_LINE_SELECTOR,
  EDITOR_TEXT_BLOCK_SELECTOR,
  EDITOR_TEXT_BOX_SELECTOR,
  EDITOR_TEXT_RUN_SELECTOR,
  editorTextBoxFacesLoaded,
  editorTextGeometryHash,
  measureEditorGlyphGeometry,
  readEditorPaintedTextBox,
} from '@/utils/editor-text-box/editorTextBoxAdapter'
import type {
  EditorGeometryRect,
  EditorGlyphGeometry,
  EditorGlyphSource,
  EditorPaintedTextBox,
  EditorTextLineGeometry,
} from '@/utils/editor-text-box/editorTextBoxAdapter'
import { isEditorProbeArmed } from './editorProbe'
import { tallestRunElementOf } from './textGeometryTallestRun'
import { textGeometryDigestOf } from '@messenger/utils'
import type { GrowthCeilingKept, TextGeometryDigest } from '@messenger/utils'
import {
  offsetPageRect,
  pageFrameOf,
  roundPageRect,
  toPageSpace,
  type PageFrame,
  type PageRect,
} from './pageSpace'
import {
  EDITOR_TEXT_BOX_ATTR,
  EDITOR_TEXT_EDITING_ATTR,
  EDITOR_TEXT_FIT_SETTLED_ATTR,
  EDITOR_TEXT_FIT_VERSION_ATTR,
  EDITOR_TEXT_HOST_ATTR,
  ITEM_RECT_ATTR,
  ITEM_SELECTED_ATTR,
  PAGE_ID_ATTR,
  PAGE_SURFACE_ATTR,
  PAGE_ZOOM_ATTR,
  TEXT_ANATOMY_ATTR,
  TEXT_BLOCKS_ATTR,
  TEXT_BOUNDED_BY_ATTR,
  TEXT_BOUNDED_BY_NONE,
  TEXT_CEILING_ATTR,
  TEXT_CEILING_FROM_ATTR,
  TEXT_CONTENT_HEIGHT_ATTR,
  TEXT_FACES_PENDING_ATTR,
  TEXT_FACES_SETTLED_ATTR,
  TEXT_LOAD_FIT_ATTR,
  TEXT_LOAD_HEIGHT_ATTR,
  TEXT_FIT_PASSES_ATTR,
  TEXT_ITEM_ATTR,
  TEXT_ITEM_STATE_ATTR,
  TEXT_KEPT_BY_ATTR,
  TEXT_KEPT_CEILING_ATTR,
  TEXT_LINE_HEIGHT_ATTR,
  TEXT_LOADING_ATTR,
  TEXT_MAX_SIZE_PT_ATTR,
  TEXT_MEASURE_ATTR,
  TEXT_MEASURED_AT_ONE_ATTR,
  TEXT_RUN_LINE_HEIGHT_ATTR,
  TEXT_SIZING_ATTR,
  TEXT_STYLES_SETTLED_ATTR,
  TEXT_WRITES_ATTR,
  parseItemRectAttribute,
} from './textGeometryAttributes'

export type TextGeometryRect = EditorGeometryRect
export type TextGeometryGlyph = EditorGlyphGeometry
export type TextGeometryLine = EditorTextLineGeometry
export type TextGeometryKind = 'tinymce' | 'lotus'
export type TextGeometrySurface = 'editor' | 'preview' | 'pdf' | 'unknown'

export type TextGeometryEdges = {
  top: number
  right: number
  bottom: number
  left: number
}

export type TextGeometryFit = {
  scale: number | null
  overflowing: boolean
  inkEscape: boolean
  basePad: TextGeometryEdges | null
  reserve: TextGeometryEdges
  version: number | null
  /**
   * The text's height at its authored sizes, outer px, as the load fit
   * measured it with the frame at auto (`data-text-content-height`): the
   * frame that would hold the text at fit scale 1. Null before a load
   * result or for a box with nothing to measure.
   */
  contentHeightAtOne: number | null
  /**
   * The box's load result, stamped on its root by the read-only fit with the
   * same three attributes a TinyMCE box stamps: the frame the fit fitted
   * into (the row's), the room the page leaves under the box (null when the
   * surface could not say), and what bounds that room (an item id, `margin`,
   * `fold`, `preexisting`; null for the page bottom alone or before a load
   * result).
   */
  loadHeight: number | null
  ceiling: number | null
  boundedBy: string | null
  /** The frame the room was measured from (`data-text-ceiling-from`): its x, y and width; null when unstamped. */
  ceilingFrom: TextGeometryCeilingFrom | null
  /** The room a `preexisting` box's stored frame is past, with what bounds it and one line of the text; null for every other result. */
  kept: TextGeometryKeptCeiling | null
  /**
   * The text sizing the read-only fit ran under (`data-text-sizing`): `shrink`
   * or `fill`; null before a load result.
   */
  sizing: 'shrink' | 'fill' | null
  /** The maximum size in pt a Fill text box may paint (`data-text-max-size-pt`); null for no limit or before a load result. */
  maxFontSizePt: number | null
}

/**
 * What a box flagged `preexisting` was saved past, as the surface stamped
 * it: the room in px, what bounds it (an item id, `margin` or `fold`), and
 * one line of the box's text in px (null when the surface measured none) —
 * every number a refit into that room would use, in the shape the rule
 * itself reports it.
 */
export type TextGeometryKeptCeiling = GrowthCeilingKept

/** The box the room was measured from, as the surface stamped it: two surfaces a pixel apart name their sources. */
export type TextGeometryCeilingFrom = { x: number; y: number; width: number }

export type TextGeometryLayout = {
  clientWidth: number
  clientHeight: number
  scrollWidth: number
  scrollHeight: number
}

export type TextGeometryBlockFont = {
  family: string
  sizePx: number
  lineHeightPx: number | null
  loaded: boolean | null
}

export type TextGeometryAutosize = {
  fontSizesPt: number[]
  lineHeights: string[]
  contentWidth: number
  contentHeight: number
  itemState: 'loading' | 'idle' | 'content-overflowing'
  facesPending: boolean
  /** Every face the box's text resolves to reports loaded. */
  facesLoaded: boolean
  /** What the settle before the load result concluded: `loaded`, `error:<families>` or `undeclared:<families>`; null before it. */
  facesSettled: string | null
  /** Whether the content styles had reached the body and every run before the result: `ready`, `ready-after:<frames>:<body|run>` or `unstyled:<frames>:<body|run>`; null before it. */
  stylesSettled: string | null
  /** The tallest run's and its block's computed line-height and font-size in px when the load result read the body, `run=<lineHeight>/<fontSize>;block=<lineHeight>/<fontSize>` (`normal` in place of a line-height at `normal`, `block=none` in the body itself); null before it. */
  runLineHeight: string | null
  /** The body's blocks when the load result read it: `<children>:<bogus>:<head of the last child's markup>`; null before it. */
  blocks: string | null
  /** The body's anatomy at that instant (padding, root nodes, per-block heights and line counts, chips taking height, the last block's inner head); null before the result. */
  anatomy: string | null
  /** Load results painted for the current text: 0 until the one lands, then 1. */
  passes: number
  writes: number
  /** The painted body's height at the authored sizes, before any fit. */
  contentHeightAtOne: number | null
  /** The measurer's reading of that same HTML: within 1px of `contentHeightAtOne` when it measures like it paints. */
  measuredAtOne: number | null
  /** The frame the box painted on load: the row's height (its text's height for a row with none). */
  loadHeight: number | null
  /** The room the page leaves under the box; null before the result, or when the surface could not say. */
  ceiling: number | null
  /** A blocking item's id, `margin`, `fold` or `preexisting`; null when only the page bottom bounds the box. */
  boundedBy: string | null
  /** What the result did with the text: `kept`, `grace:<overflow px>` or `fitted`; null before it. */
  loadFit: string | null
  /** The frame the room was measured from (`data-text-ceiling-from`); null when unstamped. */
  ceilingFrom: TextGeometryCeilingFrom | null
  /** The room a `preexisting` box's stored frame is past, with what bounds it and one line of the text; null for every other result. */
  kept: TextGeometryKeptCeiling | null
}

export type TextGeometryBox = {
  itemId: string
  kind: TextGeometryKind
  pageIndex: number
  pageId: string | null
  editing: boolean
  /**
   * The item is selected on the canvas (`data-item-selected` on the item
   * wrapper the box paints inside). False wherever nothing selects — the
   * preview and the PDF — so a reading names the state it was taken in
   * without a spec reaching into the DOM for it.
   */
  selected: boolean
  settled: boolean
  faces: { loaded: boolean; families: string[] }
  atom: PageRect | null
  frame: PageRect
  content: PageRect | null
  layout: TextGeometryLayout | null
  blockFont: TextGeometryBlockFont | null
  textFont: TextGeometryBlockFont | null
  strutMatchesText: boolean | null
  linePitchPx: number | null
  fit: TextGeometryFit | null
  autosize: TextGeometryAutosize | null
  /**
   * Every painted style axis of a Lotus box — box chrome, paragraph axes,
   * run axes — read by the engine's own reader (`readEditorPaintedTextBox`),
   * the same one the sizing inspector shows; null for a TinyMCE item.
   */
  painted: EditorPaintedTextBox | null
  lines: TextGeometryLine[]
  glyphs: TextGeometryGlyph[]
  /**
   * The painted stroke of every rule block in a Lotus box, in the page's
   * content space like the glyphs' ink: the bleed check the worker runs on
   * its proof joins them to the glyph ink, as the engine's own ink rect
   * does. Empty for a TinyMCE item.
   */
  rules: PageRect[]
  complete: boolean
  text: string
  textHash: string
}

export type TextGeometrySnapshot = {
  surface: TextGeometrySurface
  pageIds: Array<string | null>
  zoom: number | null
  zoomAtom: number | null
  devicePixelRatio: number
  platform: string
  userAgent: string
  fontsStatus: string
  takenAt: number
  boxes: TextGeometryBox[]
}

/**
 * One Lotus box's painted styles alone, with no glyph measurement: cheap
 * enough for a sampler to read every animation frame.
 */
export type TextGeometryPainted = {
  itemId: string
  editing: boolean
  /** Every face the box paints with is loaded (the engine's own check). */
  facesLoaded: boolean
  painted: EditorPaintedTextBox
}

export const TEXT_GEOMETRY_QUERY_PARAM = 'editorTextGeometry'

type GeometryWindow = Window & {
  __EDITOR_TEXT_GEOMETRY__?: boolean
  __editorTextGeometry?: {
    snapshot: (itemId?: string) => TextGeometrySnapshot
    painted: (itemId: string) => TextGeometryPainted | null
  }
}

// The autosize measurer wears the body's class while it measures; the probe
// never mistakes it for the body.
const TINYMCE_BODY_SELECTOR = `.mce-content-body:not([${TEXT_MEASURE_ATTR}])`
/**
 * The class TinyMCE puts on the body of the inline editor that has focus.
 * A classic item's body carries `contenteditable` from the moment its
 * editor initialises — on the preview and inside the PDF renderer as much
 * as on the canvas — so the attribute says nothing about who is editing;
 * this class does.
 */
const TINYMCE_EDIT_FOCUS_SELECTOR = '.mce-content-body.mce-edit-focus'
const TINYMCE_BLOCK_SELECTOR =
  'p, h1, h2, h3, h4, h5, h6, li, td, th, blockquote, pre'
const FONT_SIZE_PT_PATTERN = /font-size\s*:\s*([\d.]+)pt/gi
const LINE_HEIGHT_PATTERN = /line-height\s*:\s*([\d.]+[a-zA-Z%]*)/gi

const roundGlyph = (
  glyph: TextGeometryGlyph,
  origin: { x: number; y: number },
): TextGeometryGlyph => ({
  ...glyph,
  sizePx: Math.round(glyph.sizePx * 100) / 100,
  advance: roundPageRect(offsetPageRect(glyph.advance, origin)),
  ink: glyph.ink ? roundPageRect(offsetPageRect(glyph.ink, origin)) : null,
})

const roundLine = (
  line: TextGeometryLine,
  origin: { x: number; y: number },
): TextGeometryLine => ({
  ...line,
  rect: roundPageRect(offsetPageRect(line.rect, origin)),
})

const textNodesUnder = (root: Node): Text[] => {
  const nodes: Text[] = []
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT)
  let node = walker.nextNode()
  while (node) {
    if ((node as Text).data !== '') nodes.push(node as Text)
    node = walker.nextNode()
  }
  return nodes
}

const lotusRules = (box: HTMLElement, frame: PageFrame): PageRect[] =>
  Array.from(box.querySelectorAll<HTMLElement>(EDITOR_RULE_LINE_SELECTOR))
    .map((line) => line.getBoundingClientRect())
    .filter((rect) => rect.width > 0 && rect.height > 0)
    .map((rect) => roundPageRect(toPageSpace(rect, frame)))

const lotusSources = (box: HTMLElement): EditorGlyphSource[] => {
  const sources: EditorGlyphSource[] = []
  const blocks = Array.from(box.querySelectorAll(EDITOR_TEXT_BLOCK_SELECTOR))
  blocks.forEach((block, index) => {
    block
      .querySelectorAll<HTMLElement>(
        `${EDITOR_TEXT_RUN_SELECTOR}:not([${EDITOR_FILLER_ATTR}]):not([${EDITOR_EMPTY_FIELD_ATTR}])`,
      )
      .forEach((run) => {
        for (const node of textNodesUnder(run))
          sources.push({ block: index, node })
      })
  })
  return sources
}

const tinymceSources = (body: HTMLElement): EditorGlyphSource[] => {
  const blockIndex = new Map<Element, number>()
  const blockOf = (node: Text): Element => {
    const el = node.parentElement
    if (!el) return body
    const block = el.closest(TINYMCE_BLOCK_SELECTOR)
    if (block && body.contains(block) && block !== body) return block
    let candidate: Element | null = el
    while (candidate && candidate.parentElement !== body)
      candidate = candidate.parentElement
    return candidate ?? body
  }
  return textNodesUnder(body).map((node) => {
    const block = blockOf(node)
    let index = blockIndex.get(block)
    if (index === undefined) {
      index = blockIndex.size
      blockIndex.set(block, index)
    }
    return { block: index, node }
  })
}

const sourcesText = (sources: readonly EditorGlyphSource[]): string => {
  const blocks: string[] = []
  for (const { block, node } of sources)
    blocks[block] = (blocks[block] ?? '') + node.data
  return blocks.map((text) => text ?? '').join('\n')
}

const uniqueMatches = (html: string, pattern: RegExp): string[] =>
  Array.from(new Set(Array.from(html.matchAll(pattern), (m) => m[1])))

/** Every font size in a TinyMCE box's HTML, in pt, ascending: the sizes the fit loop moves. */
export const fontSizesPtOf = (html: string): number[] =>
  Array.from(
    new Set(uniqueMatches(html, FONT_SIZE_PT_PATTERN).map(Number)),
  ).sort((a, b) => a - b)

type Geometry = {
  lines: TextGeometryLine[]
  glyphs: TextGeometryGlyph[]
  complete: boolean
  faces: { loaded: boolean; families: string[] }
}

const measureGeometry = (
  originEl: HTMLElement,
  sources: readonly EditorGlyphSource[],
  frame: PageFrame,
  facesLoadedHint: boolean,
): Geometry => {
  const reading = measureEditorGlyphGeometry(originEl, sources)
  const origin = toPageSpace(originEl.getBoundingClientRect(), frame)
  if (!reading)
    return {
      lines: [],
      glyphs: [],
      complete: sources.length === 0,
      faces: { loaded: facesLoadedHint, families: [] },
    }
  const glyphs = reading.glyphs.map((glyph) => roundGlyph(glyph, origin))
  return {
    lines: reading.lines.map((line) => roundLine(line, origin)),
    glyphs,
    complete: reading.complete,
    faces: {
      loaded: facesLoadedHint && glyphs.every((g) => g.loaded !== false),
      families: Array.from(new Set(glyphs.map((g) => g.family))),
    },
  }
}

const px = (value: string): number =>
  Math.round((Number.parseFloat(value) || 0) * 1000) / 1000

const edgesOf = (el: HTMLElement): TextGeometryEdges => {
  const cs = getComputedStyle(el)
  return {
    top: px(cs.paddingTop),
    right: px(cs.paddingRight),
    bottom: px(cs.paddingBottom),
    left: px(cs.paddingLeft),
  }
}

const blockFontOf = (el: Element | null): TextGeometryBlockFont | null => {
  if (!el) return null
  const cs = getComputedStyle(el)
  const lineHeight = Number.parseFloat(cs.lineHeight)
  let loaded: boolean | null = null
  try {
    loaded = document.fonts.check(
      `${cs.fontStyle} ${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`,
    )
  } catch {
    loaded = null
  }
  return {
    family: cs.fontFamily,
    sizePx: px(cs.fontSize),
    lineHeightPx: Number.isFinite(lineHeight) ? px(cs.lineHeight) : null,
    loaded,
  }
}

const firstFamilyOf = (fontFamily: string): string =>
  (fontFamily.split(',')[0] ?? '').trim().replace(/^["']|["']$/g, '')

const strutMatchesTextOf = (
  block: TextGeometryBlockFont | null,
  text: TextGeometryBlockFont | null,
): boolean | null => {
  if (!block || !text) return null
  if (block.sizePx === 0) return true
  return (
    firstFamilyOf(block.family) === firstFamilyOf(text.family) &&
    Math.abs(block.sizePx - text.sizePx) < 0.01
  )
}

const linePitchOf = (lines: readonly TextGeometryLine[]): number | null => {
  if (lines.length < 2) return null
  const pitches = lines
    .slice(1)
    .map((line, i) => line.rect.y - lines[i].rect.y)
    .sort((a, b) => a - b)
  const middle = Math.floor(pitches.length / 2)
  const median =
    pitches.length % 2 === 0
      ? (pitches[middle - 1] + pitches[middle]) / 2
      : pitches[middle]
  return Math.round(median * 1000) / 1000
}

const layoutOf = (el: HTMLElement): TextGeometryLayout => ({
  clientWidth: el.clientWidth,
  clientHeight: el.clientHeight,
  scrollWidth: el.scrollWidth,
  scrollHeight: el.scrollHeight,
})

const numberOrNull = (value: string | undefined): number | null => {
  if (value === undefined) return null
  const n = Number(value)
  return Number.isFinite(n) ? n : null
}

/**
 * Null only when the attribute is absent: the surface writes the stamp's
 * empty height slot as nothing, and `parseItemRectAttribute` reads an empty
 * slot as 0, so a zero inside the stamp is a coordinate, never "unrecorded".
 * A surface stamps it only with a resolved ceiling, whose x, y and width are
 * all finite.
 */
const ceilingFromOf = (
  value: string | null | undefined,
): TextGeometryCeilingFrom | null => {
  const rect = parseItemRectAttribute(value ?? null)
  return rect ? { x: rect.x, y: rect.y, width: rect.width } : null
}

const sizingOf = (
  value: string | null | undefined,
): TextGeometryFit['sizing'] =>
  value === 'shrink' || value === 'fill' ? value : null

const boundedByOf = (value: string | null | undefined): string | null =>
  value === null || value === undefined || value === TEXT_BOUNDED_BY_NONE
    ? null
    : value

/**
 * The room a surface stamped beside `preexisting`, or null when the
 * attributes are absent or the room does not parse as the stamp writes it.
 */
const keptCeilingOf = (
  el: HTMLElement | null,
): TextGeometryKeptCeiling | null => {
  const ceiling = numberOrNull(
    el?.getAttribute(TEXT_KEPT_CEILING_ATTR) ?? undefined,
  )
  if (ceiling === null) return null
  return {
    ceiling,
    boundedBy: boundedByOf(el?.getAttribute(TEXT_KEPT_BY_ATTR)),
    lineHeight: numberOrNull(
      el?.getAttribute(TEXT_LINE_HEIGHT_ATTR) ?? undefined,
    ),
  }
}

const tinymceItemStateOf = (
  container: HTMLElement | null,
): TextGeometryAutosize['itemState'] => {
  const state = container?.getAttribute(TEXT_ITEM_STATE_ATTR)
  if (state === 'idle' || state === 'content-overflowing') return state
  if (state === 'loading') return 'loading'
  return container?.getAttribute(TEXT_LOADING_ATTR) === 'true'
    ? 'loading'
    : 'idle'
}

/**
 * Whether the canvas has the box's item selected: the state lives on the
 * item wrapper the box paints inside, one level of the DOM above both kinds
 * of box. A surface with no selection — the preview, the PDF — has no
 * wrapper carrying it, which reads false.
 */
const selectedOf = (root: HTMLElement): boolean =>
  root
    .closest<HTMLElement>(`[${ITEM_SELECTED_ATTR}]`)
    ?.getAttribute(ITEM_SELECTED_ATTR) === 'true'

/** The element the engine fitted: the live editing host, or the resting paint's box. */
const fittedOf = (root: HTMLElement): HTMLElement | null =>
  root.querySelector<HTMLElement>(`[${EDITOR_TEXT_HOST_ATTR}]`) ??
  root.querySelector<HTMLElement>(EDITOR_TEXT_BOX_SELECTOR)

const lotusBox = (
  root: HTMLElement,
  frame: PageFrame,
  pageIndex: number,
  pageId: string | null,
): TextGeometryBox => {
  const itemId = root.getAttribute(EDITOR_TEXT_BOX_ATTR) ?? ''
  const editing = root.hasAttribute(EDITOR_TEXT_EDITING_ATTR)
  const fitted = fittedOf(root)
  const sources = fitted ? lotusSources(fitted) : []
  const geometry = fitted
    ? measureGeometry(fitted, sources, frame, editorTextBoxFacesLoaded(fitted))
    : {
        lines: [],
        glyphs: [],
        complete: false,
        faces: { loaded: false, families: [] },
      }
  const basePadTop = numberOrNull(fitted?.dataset.basePadTop)
  const text = sourcesText(sources)
  const blockFont = blockFontOf(
    fitted?.querySelector(EDITOR_TEXT_BLOCK_SELECTOR) ?? null,
  )
  const textFont = blockFontOf(tallestRunElementOf(sources))
  return {
    itemId,
    kind: 'lotus',
    pageIndex,
    pageId,
    editing,
    selected: selectedOf(root),
    settled: root.getAttribute(EDITOR_TEXT_FIT_SETTLED_ATTR) === 'true',
    faces: geometry.faces,
    atom: parseItemRectAttribute(root.getAttribute(ITEM_RECT_ATTR)),
    frame: roundPageRect(toPageSpace(root.getBoundingClientRect(), frame)),
    content: fitted
      ? roundPageRect(toPageSpace(fitted.getBoundingClientRect(), frame))
      : null,
    layout: fitted ? layoutOf(fitted) : null,
    blockFont,
    textFont,
    strutMatchesText: strutMatchesTextOf(blockFont, textFont),
    linePitchPx: linePitchOf(geometry.lines),
    fit: fitted
      ? {
          scale: numberOrNull(fitted.dataset.fitScale),
          overflowing: fitted.dataset.overflowing === 'true',
          inkEscape: fitted.dataset.inkEscape === 'true',
          basePad:
            basePadTop === null
              ? null
              : {
                  top: basePadTop,
                  right: numberOrNull(fitted.dataset.basePadRight) ?? 0,
                  bottom: numberOrNull(fitted.dataset.basePadBottom) ?? 0,
                  left: numberOrNull(fitted.dataset.basePadLeft) ?? 0,
                },
          reserve: edgesOf(fitted),
          version: numberOrNull(
            root.getAttribute(EDITOR_TEXT_FIT_VERSION_ATTR) ?? undefined,
          ),
          contentHeightAtOne: numberOrNull(
            root.getAttribute(TEXT_CONTENT_HEIGHT_ATTR) ?? undefined,
          ),
          loadHeight: numberOrNull(
            root.getAttribute(TEXT_LOAD_HEIGHT_ATTR) ?? undefined,
          ),
          ceiling: numberOrNull(
            root.getAttribute(TEXT_CEILING_ATTR) ?? undefined,
          ),
          boundedBy: boundedByOf(root.getAttribute(TEXT_BOUNDED_BY_ATTR)),
          ceilingFrom: ceilingFromOf(root.getAttribute(TEXT_CEILING_FROM_ATTR)),
          kept: keptCeilingOf(root),
          sizing: sizingOf(root.getAttribute(TEXT_SIZING_ATTR)),
          maxFontSizePt: numberOrNull(
            root.getAttribute(TEXT_MAX_SIZE_PT_ATTR) ?? undefined,
          ),
        }
      : null,
    autosize: null,
    painted: fitted ? readEditorPaintedTextBox(fitted) : null,
    lines: geometry.lines,
    glyphs: geometry.glyphs,
    rules: fitted ? lotusRules(fitted, frame) : [],
    complete: geometry.complete,
    text,
    textHash: editorTextGeometryHash(text),
  }
}

const tinymceBox = (
  root: HTMLElement,
  frame: PageFrame,
  pageIndex: number,
  pageId: string | null,
): TextGeometryBox => {
  const itemId = root.getAttribute(TEXT_ITEM_ATTR) ?? ''
  const container = root.querySelector<HTMLElement>(`[${TEXT_LOADING_ATTR}]`)
  const body = root.querySelector<HTMLElement>(TINYMCE_BODY_SELECTOR)
  const sources = body ? tinymceSources(body) : []
  const geometry = body
    ? measureGeometry(body, sources, frame, true)
    : {
        lines: [],
        glyphs: [],
        complete: false,
        faces: { loaded: false, families: [] },
      }
  const itemState = tinymceItemStateOf(container)
  const passes = Number(container?.getAttribute(TEXT_FIT_PASSES_ATTR) ?? 0)
  const html = body?.innerHTML ?? ''
  const text = sourcesText(sources)
  const blockFont = blockFontOf(
    body?.querySelector(TINYMCE_BLOCK_SELECTOR) ?? body,
  )
  const textFont = blockFontOf(tallestRunElementOf(sources))
  return {
    itemId,
    kind: 'tinymce',
    pageIndex,
    pageId,
    editing: root.querySelector(TINYMCE_EDIT_FOCUS_SELECTOR) !== null,
    selected: selectedOf(root),
    // Settled is the one load result painted under loaded faces: a box that
    // is merely not loading has not settled until its pass has landed.
    settled:
      body !== null &&
      itemState !== 'loading' &&
      geometry.faces.loaded &&
      passes >= 1,
    faces: geometry.faces,
    atom: parseItemRectAttribute(root.getAttribute(ITEM_RECT_ATTR)),
    frame: roundPageRect(toPageSpace(root.getBoundingClientRect(), frame)),
    content: body
      ? roundPageRect(toPageSpace(body.getBoundingClientRect(), frame))
      : null,
    layout: body ? layoutOf(body) : null,
    blockFont,
    textFont,
    strutMatchesText: strutMatchesTextOf(blockFont, textFont),
    linePitchPx: linePitchOf(geometry.lines),
    fit: null,
    autosize: {
      fontSizesPt: fontSizesPtOf(html),
      lineHeights: uniqueMatches(html, LINE_HEIGHT_PATTERN).sort(),
      contentWidth: body?.offsetWidth ?? 0,
      contentHeight: body?.offsetHeight ?? 0,
      itemState,
      facesPending: container?.getAttribute(TEXT_FACES_PENDING_ATTR) === 'true',
      facesLoaded: geometry.faces.loaded,
      facesSettled: container?.getAttribute(TEXT_FACES_SETTLED_ATTR) ?? null,
      stylesSettled: container?.getAttribute(TEXT_STYLES_SETTLED_ATTR) ?? null,
      runLineHeight: container?.getAttribute(TEXT_RUN_LINE_HEIGHT_ATTR) ?? null,
      blocks: container?.getAttribute(TEXT_BLOCKS_ATTR) ?? null,
      anatomy: container?.getAttribute(TEXT_ANATOMY_ATTR) ?? null,
      passes,
      writes: Number(container?.getAttribute(TEXT_WRITES_ATTR) ?? 0),
      contentHeightAtOne: numberOrNull(
        container?.getAttribute(TEXT_CONTENT_HEIGHT_ATTR) ?? undefined,
      ),
      measuredAtOne: numberOrNull(
        container?.getAttribute(TEXT_MEASURED_AT_ONE_ATTR) ?? undefined,
      ),
      loadHeight: numberOrNull(
        container?.getAttribute(TEXT_LOAD_HEIGHT_ATTR) ?? undefined,
      ),
      ceiling: numberOrNull(
        container?.getAttribute(TEXT_CEILING_ATTR) ?? undefined,
      ),
      boundedBy: boundedByOf(container?.getAttribute(TEXT_BOUNDED_BY_ATTR)),
      loadFit: container?.getAttribute(TEXT_LOAD_FIT_ATTR) ?? null,
      ceilingFrom: ceilingFromOf(
        container?.getAttribute(TEXT_CEILING_FROM_ATTR),
      ),
      kept: keptCeilingOf(container),
    },
    painted: null,
    lines: geometry.lines,
    glyphs: geometry.glyphs,
    rules: [],
    complete: geometry.complete,
    text,
    textHash: editorTextGeometryHash(text),
  }
}

const surfaceOf = (pages: readonly HTMLElement[]): TextGeometrySurface => {
  const value = pages[0]?.getAttribute(PAGE_SURFACE_ATTR)
  return value === 'editor' || value === 'preview' || value === 'pdf'
    ? value
    : 'unknown'
}

export const snapshotTextGeometry = (itemId?: string): TextGeometrySnapshot => {
  const pages = Array.from(
    document.querySelectorAll<HTMLElement>(`[${PAGE_SURFACE_ATTR}]`),
  )
  const boxes: TextGeometryBox[] = []
  let zoom: number | null = null
  let zoomAtom: number | null = null
  pages.forEach((pageEl, pageIndex) => {
    const frame = pageFrameOf(pageEl)
    if (!frame) return
    if (zoom === null) zoom = Math.round(frame.zoom * 10000) / 10000
    if (zoomAtom === null)
      zoomAtom = numberOrNull(pageEl.getAttribute(PAGE_ZOOM_ATTR) ?? undefined)
    const pageId = pageEl.getAttribute(PAGE_ID_ATTR)
    pageEl
      .querySelectorAll<HTMLElement>(`[${EDITOR_TEXT_BOX_ATTR}]`)
      .forEach((root) => {
        if (itemId && root.getAttribute(EDITOR_TEXT_BOX_ATTR) !== itemId) return
        boxes.push(lotusBox(root, frame, pageIndex, pageId))
      })
    pageEl
      .querySelectorAll<HTMLElement>(`[${TEXT_ITEM_ATTR}]`)
      .forEach((root) => {
        if (itemId && root.getAttribute(TEXT_ITEM_ATTR) !== itemId) return
        boxes.push(tinymceBox(root, frame, pageIndex, pageId))
      })
  })
  return {
    surface: surfaceOf(pages),
    pageIds: pages.map((pageEl) => pageEl.getAttribute(PAGE_ID_ATTR)),
    zoom,
    zoomAtom,
    devicePixelRatio: window.devicePixelRatio,
    platform: navigator.platform,
    userAgent: navigator.userAgent,
    fontsStatus: document.fonts.status,
    takenAt: performance.now(),
    boxes,
  }
}

/**
 * One Lotus box's painted styles through the engine's reader, and whether
 * its faces are loaded, with nothing measured: the reading a sampler takes
 * every frame. Null when no Lotus box on the page carries the id.
 */
export const paintedTextGeometry = (
  itemId: string,
): TextGeometryPainted | null => {
  const root = document.querySelector<HTMLElement>(
    `[${EDITOR_TEXT_BOX_ATTR}="${itemId}"]`,
  )
  const fitted = root ? fittedOf(root) : null
  if (!root || !fitted) return null
  return {
    itemId,
    editing: root.hasAttribute(EDITOR_TEXT_EDITING_ATTR),
    facesLoaded: editorTextBoxFacesLoaded(fitted),
    painted: readEditorPaintedTextBox(fitted),
  }
}

/**
 * The surface's text geometry reduced to what a cross-surface comparison
 * judges, for the render request the customize page makes: the worker holds
 * its own paint against it box by box (`compareTextGeometryDigests`). Read
 * with the same readers as the probe's snapshot, armed or not.
 */
export const textGeometryDigest = (): TextGeometryDigest => {
  const snapshot = snapshotTextGeometry()
  return textGeometryDigestOf(
    snapshot.surface,
    snapshot.boxes,
    snapshot.takenAt,
  )
}

export const isTextGeometryProbeArmed = (): boolean => {
  if (typeof window === 'undefined') return false
  const geometryWindow = window as GeometryWindow
  if (geometryWindow.__EDITOR_TEXT_GEOMETRY__ === true) return true
  if (isEditorProbeArmed()) return true
  return new RegExp(`[?&]${TEXT_GEOMETRY_QUERY_PARAM}=1(&|$)`).test(
    window.location?.search ?? '',
  )
}

export const installEditorTextGeometryProbe = (): void => {
  if (!isTextGeometryProbeArmed()) return
  ;(window as GeometryWindow).__editorTextGeometry = {
    snapshot: snapshotTextGeometry,
    painted: paintedTextGeometry,
  }
}
